import { useEffect, useRef } from 'react';
import { TABLE_EDITOR_SELECTOR } from '../test-table-editor/parseClipboardTable';
import {
    BLOCK_CLIPBOARD_MIME,
    BlockClipboardPayload,
    extractPayloadFromHtml,
    parseBlockClipboardJson,
} from './blockClipboard';

interface UseBlockClipboardEventsProps {
    enabled: boolean;
    hasSelection: () => boolean;
    hasClipboard: () => boolean;
    /** Write the current selection to the event clipboard. Returns true when handled. */
    onCopy: (event: ClipboardEvent) => boolean;
    onCut: (event: ClipboardEvent) => boolean;
    /** A block payload was found on paste — insert it. */
    onPaste: (payload: BlockClipboardPayload) => void;
    getInternalClipboard: () => {
        payload: BlockClipboardPayload;
        textFingerprint: string;
    } | null;
    onDeleteSelection: () => void;
    onClearSelection: () => void;
    onDismissClipboard: () => void;
}

/**
 * Whether a block-level clipboard event should be ignored: focus is inside a
 * text editor / input / the table editor (their own copy-paste wins), or the
 * user has real text selected on the page.
 */
const shouldIgnoreEvent = (event: Event, { checkTextSelection = false } = {}) => {
    if (event.defaultPrevented) return true;

    const active = document.activeElement as HTMLElement | null;
    if (active) {
        if (
            active.tagName === 'INPUT' ||
            active.tagName === 'TEXTAREA' ||
            active.isContentEditable
        ) {
            return true;
        }
        if (active.closest(TABLE_EDITOR_SELECTOR)) return true;
    }

    if (checkTextSelection) {
        const selection = window.getSelection();
        if (selection && !selection.isCollapsed) return true;
    }

    return false;
};

/**
 * Capture-phase document copy/cut/paste + keydown listeners for block-level
 * clipboard actions. Capture guarantees priority over the bubble-phase paste
 * router (useReportPaste), which bails on defaultPrevented and therefore
 * handles only external clipboard content.
 */
export function useBlockClipboardEvents(props: UseBlockClipboardEventsProps) {
    const propsRef = useRef(props);
    propsRef.current = props;

    useEffect(() => {
        if (!props.enabled) return;

        const handleCopy = (event: ClipboardEvent) => {
            const current = propsRef.current;
            if (shouldIgnoreEvent(event, { checkTextSelection: true })) return;
            if (!current.hasSelection()) return;
            if (current.onCopy(event)) event.preventDefault();
        };

        const handleCut = (event: ClipboardEvent) => {
            const current = propsRef.current;
            if (shouldIgnoreEvent(event, { checkTextSelection: true })) return;
            if (!current.hasSelection()) return;
            if (current.onCut(event)) event.preventDefault();
        };

        const handlePaste = (event: ClipboardEvent) => {
            const current = propsRef.current;
            if (shouldIgnoreEvent(event)) return;

            const payload = resolvePastePayload(event, current.getInternalClipboard());
            if (!payload) return;

            event.preventDefault();
            current.onPaste(payload);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            const current = propsRef.current;

            if (event.key === 'Escape') {
                if (shouldIgnoreEvent(event)) return;
                if (current.hasSelection()) {
                    current.onClearSelection();
                } else if (current.hasClipboard()) {
                    current.onDismissClipboard();
                }
                return;
            }

            if (event.key === 'Delete' || event.key === 'Backspace') {
                if (shouldIgnoreEvent(event)) return;
                if (!current.hasSelection()) return;
                event.preventDefault();
                current.onDeleteSelection();
            }
        };

        document.addEventListener('copy', handleCopy, { capture: true });
        document.addEventListener('cut', handleCut, { capture: true });
        document.addEventListener('paste', handlePaste, { capture: true });
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('copy', handleCopy, { capture: true });
            document.removeEventListener('cut', handleCut, { capture: true });
            document.removeEventListener('paste', handlePaste, { capture: true });
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [props.enabled]);
}

/**
 * Paste priority chain: custom MIME → text/html embed (Safari strips custom
 * types) → internal clipboard, trusted only when text/plain still matches the
 * fingerprint written at copy time.
 */
function resolvePastePayload(
    event: ClipboardEvent,
    internal: { payload: BlockClipboardPayload; textFingerprint: string } | null,
): BlockClipboardPayload | null {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return null;

    const fromMime = parseBlockClipboardJson(clipboardData.getData(BLOCK_CLIPBOARD_MIME));
    if (fromMime) return fromMime;

    const fromHtml = extractPayloadFromHtml(clipboardData.getData('text/html'));
    if (fromHtml) return fromHtml;

    if (internal && clipboardData.getData('text/plain') === internal.textFingerprint) {
        return internal.payload;
    }

    return null;
}
